import { ChatMessage, useConversationMessages } from '@/hooks/useConversationMessages';
import { useUserProfile } from '@/hooks/useUserProfile';
import { CoachImage, sendCoachMessage } from '@/lib/coach';
import { supabase } from '@/lib/supabase';
import { uploadDocument } from '@/lib/upload';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { LinearGradient } from 'expo-linear-gradient';
import { Stack, router, useLocalSearchParams } from 'expo-router';
import { ArrowLeft, Camera, PaperPlaneTilt, ThumbsDown, ThumbsUp, X } from 'phosphor-react-native';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Alert, Animated, Image, KeyboardAvoidingView, Modal, Platform, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';
import Markdown from 'react-native-markdown-display';
import { IconButton, Text, TextInput } from 'react-native-paper';

// ✅ Détection plateforme web
const isWeb = Platform.OS === 'web';
const MAX_WIDTH = 1200;

// Design System Colors
const colors = {
  indigo700: '#4F46E5',
  indigo600: '#6366F1',
  indigo500: '#818CF8',
  indigo100: '#E0E7FF',
  indigo50: '#EEF2FF',
  slate900: '#0F172A',
  slate700: '#334155',
  slate600: '#475569',
  slate500: '#64748B',
  slate400: '#94A3B8',
  slate300: '#CBD5E1',
  slate200: '#E2E8F0',
  slate100: '#F1F5F9',
  slate50: '#F8FAFC',
  white: '#FFFFFF',
  purple600: '#7C3AED',
  emerald500: '#10B981',
  emerald100: '#D1FAE5',
  emerald700: '#065F46',
  rose500: '#F43F5E',
  rose100: '#FFE4E6',
  amber500: '#F59E0B',
  amber100: '#FEF3C7',
};

// Options de feedback négatif prédéfinies
const FEEDBACK_REASONS = [
  { id: 'wrong', label: '❌ Réponse fausse', value: 'Réponse fausse' },
  { id: 'offtopic', label: '🤷 Hors-sujet', value: 'Hors-sujet' },
  { id: 'incomplete', label: '📚 Incomplet', value: 'Incomplet' },
  { id: 'unclear', label: '🤔 Pas clair', value: 'Pas clair' },
];

// ✅ Nettoyage LaTeX pour l'affichage
function cleanLatex(text: string): string {
  return text
    .replace(/\\\[/g, '').replace(/\\\]/g, '')
    .replace(/\$\$/g, '').replace(/\$/g, '')
    .replace(/\\\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 / $2')
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '$1 / $2')
    .replace(/\\\\times/g, '×').replace(/\\times/g, '×')
    .replace(/\\\\sqrt\{([^}]+)\}/g, '√($1)').replace(/\\sqrt\{([^}]+)\}/g, '√($1)')
    .replace(/\\\\text\{([^}]+)\}/g, '$1').replace(/\\text\{([^}]+)\}/g, '$1')
    .replace(/\\\\left/g, '').replace(/\\left/g, '')
    .replace(/\\\\right/g, '').replace(/\\right/g, '')
    .replace(/\\\\/g, '')
    .replace(/\^{([^}]+)}/g, '^$1')
    .replace(/_{([^}]+)}/g, '$1')
    .replace(/\\,/g, ' ').replace(/\\;/g, ' ')
    .replace(/\\quad/g, '  ')
    .replace(/\\neq/g, '≠').replace(/\\leq/g, '≤').replace(/\\geq/g, '≥')
    .replace(/\\infty/g, '∞').replace(/\\sum/g, 'Σ').replace(/\\pi/g, 'π')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ✅ Composant pour les 3 points qui bougent (corrigé)
function TypingIndicator() {
  const dot1 = useRef(new Animated.Value(0)).current;
  const dot2 = useRef(new Animated.Value(0)).current;
  const dot3 = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const createBounce = (dot: Animated.Value) => {
      return Animated.loop(
        Animated.sequence([
          Animated.timing(dot, { toValue: -8, duration: 300, useNativeDriver: true }),
          Animated.timing(dot, { toValue: 0, duration: 300, useNativeDriver: true }),
        ])
      );
    };

    const animation1 = createBounce(dot1);
    const animation2 = createBounce(dot2);
    const animation3 = createBounce(dot3);

    animation1.start();
    const timeout2 = setTimeout(() => animation2.start(), 150);
    const timeout3 = setTimeout(() => animation3.start(), 300);

    return () => {
      animation1.stop();
      animation2.stop();
      animation3.stop();
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [dot1, dot2, dot3]);

  return (
    <View style={styles.typingContainer}>
      <View style={styles.ernestAvatarSmall}>
        <Image source={require('../../assets/images/ernest.png')} style={styles.ernestImageSmall} />
      </View>
      <View style={styles.typingBubble}>
        <Animated.View style={[styles.typingDot, { transform: [{ translateY: dot1 }] }]} />
        <Animated.View style={[styles.typingDot, { transform: [{ translateY: dot2 }] }]} />
        <Animated.View style={[styles.typingDot, { transform: [{ translateY: dot3 }] }]} />
      </View>
    </View>
  );
}

// Composant pour le chargement de l'upload
function UploadingIndicator() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 0.95) return prev;
        return prev + 0.03 + Math.random() * 0.04;
      });
    }, 400);

    return () => clearInterval(progressInterval);
  }, []);

  return (
    <View style={styles.uploadingContainer}>
      <View style={styles.ernestAvatarSmall}>
        <Image source={require('../../assets/images/ernest.png')} style={styles.ernestImageSmall} />
      </View>
      <View style={styles.uploadingBubble}>
        <View style={styles.uploadingRow}>
          <ActivityIndicator size="small" color={colors.indigo600} />
          <View style={styles.uploadingTextContainer}>
            <Text style={styles.uploadingTitle}>J'analyse ton cours...</Text>
            <Text style={styles.uploadingPercent}>{Math.round(progress * 100)}%</Text>
          </View>
        </View>
      </View>
    </View>
  );
}

// Composant pour l'effet typewriter
function TypewriterText({ content, onComplete }: { content: string; onComplete?: () => void }) {
  const [displayedContent, setDisplayedContent] = useState('');

  useEffect(() => {
    setDisplayedContent('');
    
    let currentIndex = 0;
    const chunkSize = 3;
    const speed = 15;

    const timer = setInterval(() => {
      if (currentIndex < content.length) {
        const nextIndex = Math.min(currentIndex + chunkSize, content.length);
        setDisplayedContent(content.slice(0, nextIndex));
        currentIndex = nextIndex;
      } else {
        clearInterval(timer);
        onComplete?.();
      }
    }, speed);

    return () => clearInterval(timer);
  }, [content]);

  return (
    <Markdown style={markdownStyles}>
      {cleanLatex(displayedContent)}
    </Markdown>
  );
}

// Styles pour le Markdown
const markdownStyles = {
  body: { fontSize: 15, lineHeight: 22, color: colors.slate900 },
  strong: { fontWeight: '700', color: colors.slate900 },
  bullet_list: { marginVertical: 8 },
  ordered_list: { marginVertical: 8 },
  list_item: { marginVertical: 4 },
  paragraph: { marginVertical: 6 },
  heading1: { fontSize: 20, fontWeight: '700', color: colors.slate900, marginVertical: 8 },
  heading2: { fontSize: 18, fontWeight: '700', color: colors.slate900, marginVertical: 6 },
  heading3: { fontSize: 16, fontWeight: '700', color: colors.slate900, marginVertical: 4 },
  blockquote: { backgroundColor: colors.indigo50, borderLeftColor: colors.indigo600, borderLeftWidth: 4, paddingHorizontal: 12, paddingVertical: 8, marginVertical: 8, borderRadius: 8 },
  code_inline: { backgroundColor: colors.slate100, color: colors.indigo600, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, fontSize: 14 },
  fence: { backgroundColor: colors.slate100, padding: 12, borderRadius: 8, marginVertical: 8 },
};

// ✅ Composant Modal pour le feedback négatif
function NegativeFeedbackModal({
  visible,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  visible: boolean;
  onClose: () => void;
  onSubmit: (reason: string, comment: string) => void;
  isSubmitting: boolean;
}) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState('');

  const handleSubmit = () => {
    const reason = selectedReason || '';
    onSubmit(reason, comment);
    setSelectedReason(null);
    setComment('');
  };

  const handleClose = () => {
    setSelectedReason(null);
    setComment('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Qu'est-ce qui n'allait pas ?</Text>
            <TouchableOpacity onPress={handleClose} style={styles.modalCloseButton}>
              <X size={24} color={colors.slate500} weight="bold" />
            </TouchableOpacity>
          </View>

          <View style={styles.reasonsContainer}>
            {FEEDBACK_REASONS.map((reason) => (
              <TouchableOpacity
                key={reason.id}
                style={[
                  styles.reasonButton,
                  selectedReason === reason.value && styles.reasonButtonSelected,
                ]}
                onPress={() => setSelectedReason(
                  selectedReason === reason.value ? null : reason.value
                )}
                activeOpacity={0.7}
              >
                <Text style={[
                  styles.reasonButtonText,
                  selectedReason === reason.value && styles.reasonButtonTextSelected,
                ]}>
                  {reason.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.commentContainer}>
            <Text style={styles.commentLabel}>Détails (optionnel)</Text>
            <TextInput
              style={styles.commentInput}
              placeholder="Explique ce qui n'allait pas..."
              placeholderTextColor={colors.slate400}
              value={comment}
              onChangeText={setComment}
              multiline
              numberOfLines={3}
              maxLength={500}
              mode="outlined"
              outlineColor={colors.slate200}
              activeOutlineColor={colors.indigo600}
              textColor={colors.slate900}
            />
          </View>

          <View style={styles.modalButtons}>
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={handleClose}
              activeOpacity={0.7}
            >
              <Text style={styles.modalCancelButtonText}>Annuler</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.modalSubmitButton,
                (!selectedReason && !comment.trim()) && styles.modalSubmitButtonDisabled,
              ]}
              onPress={handleSubmit}
              disabled={(!selectedReason && !comment.trim()) || isSubmitting}
              activeOpacity={0.8}
            >
              {isSubmitting ? (
                <ActivityIndicator size="small" color={colors.white} />
              ) : (
                <Text style={styles.modalSubmitButtonText}>Envoyer</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

// ✅ Composant pour les boutons de feedback
function FeedbackButtons({ 
  messageId, 
  conversationId,
  initialFeedback 
}: { 
  messageId: string; 
  conversationId: string;
  initialFeedback?: 'positive' | 'negative' | null;
}) {
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(initialFeedback || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNegativeModal, setShowNegativeModal] = useState(false);

  const submitFeedback = async (rating: 'positive' | 'negative', comment?: string) => {
    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        if (isWeb) {
          window.alert('Tu dois être connecté pour donner ton avis.');
        } else {
          Alert.alert('Erreur', 'Tu dois être connecté pour donner ton avis.');
        }
        return;
      }

      if (feedback) {
        await supabase
          .from('feedbacks')
          .delete()
          .eq('message_id', messageId)
          .eq('user_id', user.id);
      }

      const { error } = await supabase
        .from('feedbacks')
        .insert({
          user_id: user.id,
          message_id: messageId,
          coach_conversation_id: conversationId,
          rating: rating,
          comment: comment || null,
        });

      if (error) {
        console.error('Feedback error:', error);
        if (isWeb) {
          window.alert('Impossible d\'enregistrer ton avis.');
        } else {
          Alert.alert('Erreur', 'Impossible d\'enregistrer ton avis.');
        }
        return;
      }

      setFeedback(rating);
    } catch (error) {
      console.error('Feedback error:', error);
      if (isWeb) {
        window.alert('Une erreur est survenue.');
      } else {
        Alert.alert('Erreur', 'Une erreur est survenue.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePositiveFeedback = () => {
    if (isSubmitting || feedback === 'positive') return;
    submitFeedback('positive');
  };

  const handleNegativeFeedback = () => {
    if (isSubmitting) return;
    if (feedback === 'negative') return;
    setShowNegativeModal(true);
  };

  const handleNegativeSubmit = (reason: string, comment: string) => {
    const fullComment = reason 
      ? (comment.trim() ? `${reason}: ${comment.trim()}` : reason)
      : comment.trim();
    
    submitFeedback('negative', fullComment);
    setShowNegativeModal(false);
  };

  return (
    <>
      <View style={styles.feedbackContainer}>
        <TouchableOpacity
          style={[
            styles.feedbackButton,
            feedback === 'positive' && styles.feedbackButtonPositiveActive,
          ]}
          onPress={handlePositiveFeedback}
          disabled={isSubmitting}
          activeOpacity={0.7}
        >
          <ThumbsUp 
            size={16} 
            color={feedback === 'positive' ? colors.emerald700 : colors.slate400} 
            weight={feedback === 'positive' ? 'fill' : 'regular'} 
          />
        </TouchableOpacity>
        
        <TouchableOpacity
          style={[
            styles.feedbackButton,
            feedback === 'negative' && styles.feedbackButtonNegativeActive,
          ]}
          onPress={handleNegativeFeedback}
          disabled={isSubmitting}
          activeOpacity={0.7}
        >
          <ThumbsDown 
            size={16} 
            color={feedback === 'negative' ? colors.rose500 : colors.slate400} 
            weight={feedback === 'negative' ? 'fill' : 'regular'} 
          />
        </TouchableOpacity>
      </View>

      <NegativeFeedbackModal
        visible={showNegativeModal}
        onClose={() => setShowNegativeModal(false)}
        onSubmit={handleNegativeSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
}

export default function ConversationScreen() {
  const { id, title, diploma, subjectCode } = useLocalSearchParams();
  const { messages, loading } = useConversationMessages(id as string);
  const { profile } = useUserProfile();
  const [inputMessage, setInputMessage] = useState('');
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<string | null>(null);
  const [pendingImages, setPendingImages] = useState<CoachImage[]>([]);  // ✅ AJOUT VISION
  const scrollViewRef = useRef<ScrollView>(null);

  const firstName = profile?.full_name?.split(' ')[0] || '';

  useEffect(() => {
    setLocalMessages(messages);
  }, [messages]);

  useEffect(() => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd({ animated: true });
    }, 100);
  }, [localMessages, isTyping, isUploading]);

  useEffect(() => {
    if (streamingMessageId) {
      const scrollInterval = setInterval(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 200);
      return () => clearInterval(scrollInterval);
    }
  }, [streamingMessageId]);

  const handleAttachment = () => {
    if (isWeb) {
      pickDocument();
    } else {
      Alert.alert(
        'Ajouter ton cours',
        'Comment veux-tu ajouter ton cours ?',
        [
          { text: 'Prendre en photo', onPress: () => pickImage('camera') },
          { text: 'Choisir une photo', onPress: () => pickImage('gallery') },
          { text: 'Fichier (PDF, PPT, Word)', onPress: () => pickDocument() },
          { text: 'Annuler', style: 'cancel' },
        ]
      );
    }
  };

  // ✅ MODIFIÉ: pickImage envoie maintenant l'image en base64 pour la vision
  const pickImage = async (source: 'camera' | 'gallery') => {
    try {
      let result;

      if (source === 'camera') {
        const permission = await ImagePicker.requestCameraPermissionsAsync();
        if (!permission.granted) {
          if (isWeb) {
            window.alert("Autorise l'accès à la caméra pour prendre une photo de ton cours.");
          } else {
            Alert.alert('Permission requise', "Autorise l'accès à la caméra pour prendre une photo de ton cours.");
          }
          return;
        }
        result = await ImagePicker.launchCameraAsync({ mediaTypes: ['images'], quality: 0.8 });
      } else {
        const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (!permission.granted) {
          if (isWeb) {
            window.alert("Autorise l'accès à la galerie pour sélectionner une photo.");
          } else {
            Alert.alert('Permission requise', "Autorise l'accès à la galerie pour sélectionner une photo.");
          }
          return;
        }
        result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.8 });
      }

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        const fileName = asset.fileName || `photo_${Date.now()}.jpg`;
        const mimeType = asset.mimeType || 'image/jpeg';

        // ✅ NOUVEAU: Lire l'image en base64 pour la vision Dify
        try {
          let base64Data: string;

          if (isWeb) {
            // Sur web, fetch le blob et convertir
            const response = await fetch(asset.uri);
            const blob = await response.blob();
            base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const readerResult = reader.result as string;
                // Retirer le préfixe "data:image/...;base64,"
                resolve(readerResult.split(',')[1]);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          } else {
            // Sur mobile, lire en base64 via fetch + blob
            const resp = await fetch(asset.uri);
            const blob = await resp.blob();
            base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const readerResult = reader.result as string;
                resolve(readerResult.split(',')[1]);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
          }

          const newImage: CoachImage = {
            base64: base64Data,
            mime_type: mimeType,
            file_name: fileName,
          };

          setPendingImages([newImage]);

          // Message de confirmation visuel
          const confirmMessage: ChatMessage = {
            id: `system-img-${Date.now()}`,
            role: 'assistant',
            content: `📸 **Photo ajoutée !**\n\nPose ta question sur cette image et je l'analyserai.`,
            created_at: new Date().toISOString(),
          };
          setLocalMessages(prev => [...prev, confirmMessage]);

        } catch (readError) {
          console.error('Error reading image as base64:', readError);
          // Fallback: utiliser l'ancien flow document upload (OCR)
          await handleUpload(asset.uri, fileName, mimeType);
        }
      }
    } catch (error) {
      console.error('Image picker error:', error);
      if (isWeb) {
        window.alert("Impossible de sélectionner l'image.");
      } else {
        Alert.alert('Erreur', "Impossible de sélectionner l'image.");
      }
    }
  };

  const pickDocument = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'application/pdf',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ],
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        await handleUpload(asset.uri, asset.name, asset.mimeType || 'application/pdf');
      }
    } catch (error) {
      console.error('Document picker error:', error);
      if (isWeb) {
        window.alert('Impossible de sélectionner le document.');
      } else {
        Alert.alert('Erreur', 'Impossible de sélectionner le document.');
      }
    }
  };

  const handleUpload = async (uri: string, fileName: string, mimeType: string) => {
    setIsUploading(true);

    try {
      const result = await uploadDocument(uri, fileName, mimeType);

      if (result.ok) {
        setCurrentDocumentId(result.document_id);

        const successMessage: ChatMessage = {
          id: `system-${Date.now()}`,
          role: 'assistant',
          content: `✅ **Cours ajouté avec succès !**\n\nJe suis prêt à répondre à tes questions sur ce document.`,
          created_at: new Date().toISOString(),
        };
        setLocalMessages(prev => [...prev, successMessage]);
      } else {
        if (isWeb) {
          window.alert(result.error);
        } else {
          Alert.alert('Erreur', result.error);
        }
      }
    } catch (error: any) {
      if (isWeb) {
        window.alert(error.message || "Une erreur est survenue lors de l'upload.");
      } else {
        Alert.alert('Erreur', error.message || "Une erreur est survenue lors de l'upload.");
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isTyping || isUploading) return;

    const userMessageContent = inputMessage.trim();

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: 'user',
      content: userMessageContent,
      created_at: new Date().toISOString(),
    };

    setLocalMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // ✅ MODIFIÉ: Passer les images pendantes à sendCoachMessage
      const response = await sendCoachMessage({
        message: userMessageContent,
        conversationId: id as string,
        diploma: diploma as string,
        subjectCode: subjectCode as string,
        subjectLabel: title as string,
        documentId: currentDocumentId,
        images: pendingImages.length > 0 ? pendingImages : undefined,
      });

      setIsTyping(false);
      setPendingImages([]);  // ✅ Vider les images après envoi

      if (response.ok) {
        const assistantMessageId = `assistant-${Date.now()}`;
        const assistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: 'assistant',
          content: response.answer,
          created_at: new Date().toISOString(),
        };
        setLocalMessages(prev => [...prev, assistantMessage]);
        setStreamingMessageId(assistantMessageId);
      } else if (response.type === 'PAYWALL') {
        if (isWeb) {
          if (window.confirm(response.message + '\n\nPasser Premium ?')) {
            router.push('/paywall');
          }
        } else {
          Alert.alert(
            'Limite atteinte', 
            response.message, 
            [
              { 
                text: 'Passer Premium', 
                style: 'default',
                isPreferred: true,
                onPress: () => router.push('/paywall') 
              },
              { text: 'Plus tard', style: 'cancel' },
            ]
          );
        }
              // ✅ NOUVEAU: Gestion de la limite journalière Premium
      } else if (response.type === 'RATE_LIMIT') {
        if (isWeb) {
          window.alert(response.message);
        } else {
          Alert.alert('Limite journalière', response.message, [{ text: 'OK' }]);
        }
        } else {
        if (isWeb) {
          window.alert(response.error || 'Une erreur est survenue');
        } else {
          Alert.alert('Erreur', response.error || 'Une erreur est survenue', [{ text: 'OK' }]);
        }
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setIsTyping(false);
      setPendingImages([]);  // ✅ Vider aussi en cas d'erreur
      if (isWeb) {
        window.alert('Impossible de contacter Ernest. Vérifie ta connexion.');
      } else {
        Alert.alert('Erreur', 'Impossible de contacter Ernest. Vérifie ta connexion.', [{ text: 'OK' }]);
      }
    }
  };

  const handleTypewriterComplete = () => {
    setStreamingMessageId(null);
  };

  const canHaveFeedback = (message: ChatMessage) => {
    return message.role === 'assistant' && !message.id.startsWith('system-');
  };

  if (loading) {
    return (
      <View style={[styles.container, styles.centered]}>
        <ActivityIndicator size="large" color={colors.indigo600} />
        <Text style={styles.loadingText}>Chargement...</Text>
      </View>
    );
  }

  return (
    <>
      <Stack.Screen 
        options={{
          headerShown: false,
        }}
      />
      
      <KeyboardAvoidingView 
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={0}
      >
        <View style={styles.pageWrapper}>
          {/* ✅ Header avec gradient violet et branding */}
          <View style={styles.headerWrapper}>
            <LinearGradient
              colors={[colors.indigo500, colors.indigo600, colors.indigo700]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.header}
            >
              <View style={styles.headerInner}>
                <View style={styles.headerContent}>
                  <TouchableOpacity 
                    onPress={() => router.back()} 
                    style={styles.backButton}
                    activeOpacity={0.7}
                  >
                    <ArrowLeft size={24} color={colors.white} weight="bold" />
                  </TouchableOpacity>

                  <View style={styles.headerAvatarContainer}>
                    <View style={styles.avatarCircle}>
                      <Image source={require('../../assets/images/ernest.png')} style={styles.avatarImage} />
                    </View>
                  </View>
                  
                  <View style={styles.headerInfo}>
                    <Text style={styles.headerTitle}>Ernest</Text>
                    <View style={styles.headerStatus}>
                      <View style={styles.statusDot} />
                      <Text style={styles.statusText}>Coach IA</Text>
                    </View>
                  </View>
                </View>
              </View>
            </LinearGradient>
          </View>

          {/* Document Banner */}
          {currentDocumentId && (
            <View style={styles.documentBanner}>
              <View style={styles.documentBannerInner}>
                <Text style={styles.documentBannerText}>📄 Cours chargé</Text>
                <IconButton
                  icon="close"
                  iconColor={colors.emerald700}
                  size={18}
                  onPress={() => setCurrentDocumentId(null)}
                  style={styles.documentBannerClose}
                />
              </View>
            </View>
          )}

          {/* ✅ AJOUT: Banner image en attente */}
          {pendingImages.length > 0 && (
            <View style={[styles.documentBanner, { backgroundColor: colors.amber100 }]}>
              <View style={styles.documentBannerInner}>
                <Text style={[styles.documentBannerText, { color: colors.amber500 }]}>
                  📸 Photo prête à envoyer
                </Text>
                <IconButton
                  icon="close"
                  iconColor={colors.amber500}
                  size={18}
                  onPress={() => setPendingImages([])}
                  style={styles.documentBannerClose}
                />
              </View>
            </View>
          )}

          {/* Messages */}
          <ScrollView 
            ref={scrollViewRef}
            style={styles.messagesContainer}
            contentContainerStyle={styles.messagesContent}
            showsVerticalScrollIndicator={false}
            onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
          >
            <View style={styles.messagesInner}>
              {localMessages.length === 0 && !isTyping && !isUploading && (
                <View style={styles.welcomeContainer}>
                  <View style={styles.ernestAvatarSmall}>
                    <Image source={require('../../assets/images/ernest.png')} style={styles.ernestImageSmall} />
                  </View>
                  <View style={styles.welcomeBubble}>
                    <Text style={styles.welcomeName}>Ernest</Text>
                    <Text style={styles.welcomeText}>
                      {firstName ? `Salut ${firstName} !` : 'Salut !'} Je suis Ernest, ton coach IA.{'\n\n'}
                      Je peux t'aider à comprendre ton cours ou t'accompagner sur un exercice 😉.{'\n\n'}
                      Qu'est-ce que tu veux travailler ?
                    </Text>
                  </View>
                </View>
              )}

              {localMessages.map((message) => (
                <View key={message.id}>
                  {message.role === 'assistant' ? (
                    <View style={styles.assistantRow}>
                      <View style={styles.ernestAvatarSmall}>
                        <Image source={require('../../assets/images/ernest.png')} style={styles.ernestImageSmall} />
                      </View>
                      <View style={styles.assistantBubbleContainer}>
                        <View style={styles.assistantBubble}>
                          <Text style={styles.assistantName}>Ernest</Text>
                          {message.id === streamingMessageId ? (
                            <TypewriterText content={message.content} onComplete={handleTypewriterComplete} />
                          ) : (
                            <Markdown style={markdownStyles}>{cleanLatex(message.content)}</Markdown>
                          )}
                        </View>
                        {message.id !== streamingMessageId && canHaveFeedback(message) && (
                          <FeedbackButtons 
                            messageId={message.id} 
                            conversationId={id as string}
                          />
                        )}
                      </View>
                    </View>
                  ) : (
                    <View style={styles.userBubble}>
                      <Text style={styles.userText}>{message.content}</Text>
                    </View>
                  )}
                </View>
              ))}

              {isTyping && <TypingIndicator />}
              {isUploading && <UploadingIndicator />}
            </View>
          </ScrollView>

          {/* Input */}
          <View style={styles.inputContainer}>
            <View style={styles.inputInner}>
              <TouchableOpacity 
                style={styles.attachButton}
                onPress={handleAttachment}
                disabled={isTyping || isUploading}
                activeOpacity={0.7}
              >
                <LinearGradient
                  colors={[colors.indigo600, colors.purple600]}
                  style={styles.attachButtonGradient}
                >
                  <Camera size={22} color={colors.white} weight="bold" />
                </LinearGradient>
              </TouchableOpacity>

              <View style={styles.inputWrapper}>
                <TextInput
                  style={styles.input}
                  placeholder={
                    pendingImages.length > 0
                      ? "Question sur ta photo..."
                      : currentDocumentId
                        ? "Question sur ton cours..."
                        : "Pose ta question..."
                  }
                  placeholderTextColor={colors.slate400}
                  value={inputMessage}
                  onChangeText={setInputMessage}
                  multiline
                  maxLength={2000}
                  mode="outlined"
                  outlineColor={colors.slate200}
                  activeOutlineColor={colors.indigo600}
                  textColor={colors.slate900}
                />
              </View>

              <TouchableOpacity 
                style={[
                  styles.sendButton,
                  (!inputMessage.trim() || isTyping || isUploading || streamingMessageId) && styles.sendButtonDisabled
                ]}
                onPress={handleSend}
                disabled={!inputMessage.trim() || isTyping || isUploading || !!streamingMessageId}
                activeOpacity={0.8}
              >
                <LinearGradient
                  colors={(!inputMessage.trim() || isTyping || isUploading || streamingMessageId) 
                    ? [colors.slate300, colors.slate300] 
                    : [colors.indigo600, colors.purple600]
                  }
                  style={styles.sendButtonGradient}
                >
                  <PaperPlaneTilt size={22} color={colors.white} weight="fill" />
                </LinearGradient>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </KeyboardAvoidingView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.slate50,
  },
  pageWrapper: {
    flex: 1,
    width: '100%',
    maxWidth: isWeb ? MAX_WIDTH : undefined,
    alignSelf: 'center',
    backgroundColor: colors.slate50,
  },
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: colors.slate500,
    fontSize: 16,
  },

  // ✅ Header avec gradient violet
  headerWrapper: {
    backgroundColor: colors.slate50,
    width: '100%',
  },
  header: {
    paddingTop: isWeb ? 20 : 56,
    paddingBottom: 16,
    paddingHorizontal: 16,
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
  },
  headerInner: {
    width: '100%',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerAvatarContainer: {
    width: 48,
    height: 48,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarCircle: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: colors.white,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  avatarImage: {
    width: 36,
    height: 36,
    resizeMode: 'contain',
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.white,
  },
  headerStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 2,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.emerald500,
  },
  statusText: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    fontWeight: '500',
  },

  // Document Banner
  documentBanner: {
    backgroundColor: colors.emerald100,
  },
  documentBannerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 16,
  },
  documentBannerText: {
    color: colors.emerald700,
    fontSize: 14,
    fontWeight: '500',
  },
  documentBannerClose: {
    margin: 0,
    marginLeft: 4,
  },

  // Messages
  messagesContainer: {
    flex: 1,
  },
  messagesContent: {
    paddingBottom: 24,
  },
  messagesInner: {
    padding: 16,
  },

  // Welcome
  welcomeContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  welcomeBubble: {
    flex: 1,
    backgroundColor: colors.indigo50,
    borderRadius: 20,
    borderTopLeftRadius: 4,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.indigo100,
  },
  welcomeName: {
    fontSize: 14,
    fontWeight: '700',
    color: colors.indigo600,
    marginBottom: 8,
  },
  welcomeText: {
    fontSize: 15,
    lineHeight: 22,
    color: colors.slate700,
  },

  // Ernest Avatar Small
  ernestAvatarSmall: {
    width: 36,
    height: 36,
    borderRadius: 12,
    backgroundColor: colors.indigo100,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 10,
    overflow: 'visible',
  },
  ernestImageSmall: {
    width: 28,
    height: 28,
    resizeMode: 'contain',
  },

  // Assistant Message
  assistantRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  assistantBubbleContainer: {
    flex: 1,
  },
  assistantBubble: {
    backgroundColor: colors.white,
    borderRadius: 20,
    borderTopLeftRadius: 4,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.slate200,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  assistantName: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.indigo600,
    marginBottom: 8,
  },

  // Feedback Buttons
  feedbackContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 8,
    paddingLeft: 4,
  },
  feedbackButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  feedbackButtonPositiveActive: {
    backgroundColor: colors.emerald100,
  },
  feedbackButtonNegativeActive: {
    backgroundColor: colors.rose100,
  },

  // Modal Feedback
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: colors.white,
    borderRadius: 24,
    padding: 24,
    width: '100%',
    maxWidth: 400,
    shadowColor: colors.slate900,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 24,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.slate900,
  },
  modalCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.slate100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reasonsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  reasonButton: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  reasonButtonSelected: {
    backgroundColor: colors.rose100,
    borderColor: colors.rose500,
  },
  reasonButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.slate600,
  },
  reasonButtonTextSelected: {
    color: colors.rose500,
    fontWeight: '600',
  },
  commentContainer: {
    marginBottom: 20,
  },
  commentLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.slate700,
    marginBottom: 8,
  },
  commentInput: {
    backgroundColor: colors.white,
    fontSize: 15,
    minHeight: 80,
  },
  modalButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  modalCancelButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.slate100,
    alignItems: 'center',
  },
  modalCancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.slate600,
  },
  modalSubmitButton: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    backgroundColor: colors.rose500,
    alignItems: 'center',
  },
  modalSubmitButtonDisabled: {
    backgroundColor: colors.slate300,
  },
  modalSubmitButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },

  // User Message
  userBubble: {
    alignSelf: 'flex-end',
    maxWidth: '80%',
    marginBottom: 16,
    borderRadius: 20,
    borderTopRightRadius: 4,
    overflow: 'hidden',
    backgroundColor: colors.indigo600,
    padding: 14,
  },
  userText: {
    fontSize: 15,
    lineHeight: 22,
    color: colors.white,
  },

  // Typing
  typingContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  typingBubble: {
    flexDirection: 'row',
    backgroundColor: colors.white,
    borderRadius: 20,
    borderTopLeftRadius: 4,
    borderWidth: 1,
    borderColor: colors.slate200,
    padding: 16,
    gap: 6,
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.indigo600,
  },

  // Uploading
  uploadingContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  uploadingBubble: {
    flex: 1,
    backgroundColor: colors.white,
    borderRadius: 20,
    borderTopLeftRadius: 4,
    borderWidth: 1,
    borderColor: colors.slate200,
    padding: 16,
  },
  uploadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  uploadingTextContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  uploadingTitle: {
    fontSize: 15,
    color: colors.slate900,
  },
  uploadingPercent: {
    fontSize: 14,
    color: colors.indigo600,
    fontWeight: '600',
  },

  // Input
  inputContainer: {
    backgroundColor: colors.white,
    borderTopWidth: 1,
    borderTopColor: colors.slate200,
  },
  inputInner: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    padding: 12,
    paddingBottom: isWeb ? 12 : 34,
    gap: 8,
  },
  attachButton: {
    borderRadius: 22,
    overflow: 'hidden',
  },
  attachButtonGradient: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputWrapper: {
    flex: 1,
  },
  input: {
    maxHeight: 100,
    backgroundColor: colors.white,
    fontSize: 15,
  },
  sendButton: {
    borderRadius: 22,
    overflow: 'hidden',
  },
  sendButtonGradient: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendButtonDisabled: {
    opacity: 1,
  },
});
